<script setup lang="ts">
import { ref } from 'vue';
import { createInsightFeedback } from '@/api/insights';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import InsightFeedbackModal from '@/components/home/insights/feedback/insight-feedback-modal.vue';
import { FeedbackReason } from '@/components/home/insights/feedback/reasons';
import { InsightFeedbackEvaluation } from '@/types/insights';

const props = defineProps<{
  hintUuid: string;
}>();

const { createSnackbar } = useSnackbar();

const isModalOpen = ref(false);
const modalEvaluation = ref<InsightFeedbackEvaluation>('positive');
const submitting = ref(false);

const openModal = (evaluation: InsightFeedbackEvaluation) => {
  modalEvaluation.value = evaluation;
  isModalOpen.value = true;
};

const submitFeedback = async (payload: {
  evaluation: InsightFeedbackEvaluation;
  reasons: FeedbackReason[];
  comment: string;
}): Promise<void> => {
  if (submitting.value) return;
  submitting.value = true;
  try {
    await createInsightFeedback({
      hint_uuid: props.hintUuid,
      evaluation: payload.evaluation,
      reasons: payload.reasons,
      comment: payload.comment,
    });
  } catch (e) {
    createSnackbar({
      message:
        'フィードバックの送信に失敗しました。しばらくお待ちの上、再度お試しください。',
      type: 'error',
    });
    throw e;
  } finally {
    submitting.value = false;
    isModalOpen.value = false;
  }

  createSnackbar({
    message: '送信しました。いただいたご意見は改善に活かします。',
    type: 'success',
  });
};
</script>

<template>
  <div class="feedback-buttons">
    <button
      type="button"
      class="c-btn c-btn--auto c-btn--small c-outlineBtn--secondary"
      :disabled="submitting"
      @click="openModal('positive')"
      v-tooltip="{ content: '役に立った' }"
    >
      <DgrIcon name="thumbs-up" size="small" />
    </button>
    <button
      type="button"
      class="c-btn c-btn--auto c-btn--small c-outlineBtn--secondary"
      :disabled="submitting"
      @click="openModal('negative')"
      v-tooltip="{ content: '役に立たなかった' }"
    >
      <DgrIcon name="thumbs-down" size="small" />
    </button>

    <InsightFeedbackModal
      v-model:open="isModalOpen"
      :evaluation="modalEvaluation"
      :submitting="submitting"
      @submit="submitFeedback"
    />
  </div>
</template>

<style lang="scss" scoped>
.feedback-buttons {
  display: flex;
  gap: 8px;

  button {
    border: none;
    padding: 8px;

    &:not(:hover) {
      background: #fff;
    }
  }
}
</style>
